/**
 * UnifiedChat.jsx - Reusable Chat Component for Love & Dreams
 * 
 * Features:
 * - 20 prompts/day limit (invisible counter)
 * - Cross-chat context from Cosmos
 * - Session continuity (remembers yesterday)
 * - Auto-save on back button
 * - AI-generated session image on close
 * - Voice Input (Gemini Speech-to-Text)
 * - Memory Tab: View past summaries & manifest visions
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Mic, MicOff, Upload, ArrowLeft, Sparkles, AlertCircle, BookOpen, Moon, Heart, Stars, Loader2, ScrollText, MessageCircle, Scroll, ThumbsUp, ThumbsDown } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useGeminiVoiceInput } from "../hooks/useGeminiVoiceInput";
import OracleMemoryTab from "./OracleMemoryTab";
import { getAuthHeaders } from "../utils/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Theme configurations for each chat type
const CHAT_THEMES = {
    love: {
        gradient: 'linear-gradient(180deg, rgba(31, 20, 15, 0.98) 0%, rgba(20, 10, 8, 0.99) 100%)',
        accentColor: 'rgba(251, 191, 36, 0.4)',
        accentColorLight: 'rgba(251, 191, 36, 0.2)',
        textColor: '#FCD34D',
        icon: Heart,
        titleEs: 'El Oráculo del Amor',
        titleEn: 'The Love Oracle',
        subtitleEs: 'Consejero de Vínculos',
        subtitleEn: 'Relationship Advisor',
        placeholderEs: '¿Qué te inquieta del corazón?',
        placeholderEn: 'What weighs on your heart?'
    },
    dreams: {
        gradient: 'linear-gradient(180deg, rgba(25, 15, 35, 0.98) 0%, rgba(15, 8, 25, 0.99) 100%)',
        accentColor: 'rgba(167, 139, 250, 0.4)',
        accentColorLight: 'rgba(167, 139, 250, 0.2)',
        textColor: '#C4B5FD',
        icon: Stars,
        titleEs: 'El Oráculo de los Sueños',
        titleEn: 'The Dream Oracle',
        subtitleEs: 'Intérprete Onírico',
        subtitleEn: 'Dream Interpreter',
        placeholderEs: '¿Qué soñaste anoche?',
        placeholderEn: 'What did you dream last night?'
    }
};

// Message Bubble Component
const MessageBubble = ({ message, theme, onRate = null, showRating = true }) => {
    const isUser = message.role === 'user';
    const [hasRated, setHasRated] = useState(message.user_rating || null);
    
    const renderContent = (content) => {
        if (isUser) return content;
        const parts = content.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i} style={{ color: theme.textColor }}>{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    };
    
    const handleRate = async (rating) => {
        if (hasRated || !onRate) return;
        setHasRated(rating);
        await onRate(message.id || message.timestamp, rating);
    };
    
    return (
        <motion.div
            className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
        >
            <div className="max-w-[85%]">
                <div
                    className={`rounded-2xl px-4 py-3 ${isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}
                    style={{
                        background: isUser
                            ? `linear-gradient(135deg, ${theme.accentColor} 0%, ${theme.accentColorLight} 100%)`
                            : `linear-gradient(135deg, ${theme.accentColorLight} 0%, rgba(15, 23, 42, 0.4) 100%)`,
                        border: `1px solid ${isUser ? theme.accentColor : theme.accentColorLight}`,
                    }}
                >
                    {message.imagePreview && (
                        <img src={message.imagePreview} alt="Shared" className="max-h-40 rounded-lg mb-2 opacity-90" />
                    )}
                    <p className="text-sm font-inter text-white/90 whitespace-pre-wrap leading-relaxed">
                        {renderContent(message.content)}
                    </p>
                    <span className="text-[10px] text-white/30 mt-2 block">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                </div>
                
                {/* Customer rating for Oracle messages */}
                {!isUser && showRating && onRate && (
                    <motion.div 
                        className="flex items-center justify-end gap-1 mt-1 pr-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        {hasRated ? (
                            <span className="text-[10px] text-white/30 flex items-center gap-1">
                                {hasRated === 'helpful' ? '👍' : '👎'} 
                                <span>Thanks!</span>
                            </span>
                        ) : (
                            <>
                                <span className="text-[10px] text-white/30 mr-1">Helpful?</span>
                                <button
                                    onClick={() => handleRate('helpful')}
                                    className="p-1 hover:bg-green-500/20 rounded transition-colors group"
                                    data-testid="rate-helpful"
                                >
                                    <ThumbsUp className="w-3 h-3 text-white/30 group-hover:text-green-400" />
                                </button>
                                <button
                                    onClick={() => handleRate('not_helpful')}
                                    className="p-1 hover:bg-red-500/20 rounded transition-colors group"
                                    data-testid="rate-not-helpful"
                                >
                                    <ThumbsDown className="w-3 h-3 text-white/30 group-hover:text-red-400" />
                                </button>
                            </>
                        )}
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};

// Typing Indicator
const TypingIndicator = ({ theme }) => (
    <motion.div className="flex justify-start mb-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div
            className="rounded-2xl rounded-bl-md px-5 py-4"
            style={{ background: theme.accentColorLight, border: `1px solid ${theme.accentColor}` }}
        >
            <div className="flex items-center gap-2">
                <Sparkles size={14} style={{ color: theme.textColor }} className="animate-pulse" />
                <div className="flex gap-1">
                    {[0, 1, 2].map(i => (
                        <motion.div
                            key={i}
                            className="w-2 h-2 rounded-full"
                            style={{ background: theme.textColor }}
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                        />
                    ))}
                </div>
            </div>
        </div>
    </motion.div>
);

// Session Closed Overlay with Generated Image
const SessionClosedOverlay = ({ onSaveToJournal, hasSaved, theme, generatedImage, t }) => (
    <motion.div
        className="absolute inset-0 z-20 flex items-center justify-center"
        style={{ background: 'rgba(8, 12, 28, 0.95)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
    >
        <div className="text-center px-8 max-w-md">
            {generatedImage && (
                <img 
                    src={generatedImage} 
                    alt="Session visualization" 
                    className="w-48 h-48 mx-auto mb-4 rounded-xl opacity-90 object-cover"
                    style={{ border: `2px solid ${theme.accentColor}` }}
                />
            )}
            <Moon size={48} style={{ color: theme.textColor }} className="mx-auto mb-4" />
            <h3 className="font-cinzel text-xl text-white mb-3">
                {t('chat.portal_closes')}
            </h3>
            <p className="text-sm text-white/60 mb-6 leading-relaxed">
                {t('chat.session_limit_reached')}
            </p>
            {!hasSaved && (
                <button
                    onClick={onSaveToJournal}
                    className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl text-sm font-medium transition-all hover:scale-105"
                    style={{ background: theme.accentColor, border: `1px solid ${theme.textColor}`, color: theme.textColor }}
                >
                    <BookOpen size={16} />
                    {t('chat.save_to_journal')}
                </button>
            )}
            {hasSaved && (
                <p className="text-sm text-green-400 flex items-center justify-center gap-2">
                    <Sparkles size={14} />
                    {t('chat.saved_to_journal')}
                </p>
            )}
        </div>
    </motion.div>
);

// Main Unified Chat Component
export const UnifiedChat = ({ chatType, onClose, initialQuery = '' }) => {
    const { language, t } = useLanguage();
    const theme = CHAT_THEMES[chatType];
    const IconComponent = theme.icon;
    const isSpanish = language?.startsWith('es');
    
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState(initialQuery); // Pre-fill if provided
    const [isLoading, setIsLoading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imageError, setImageError] = useState(null);
    const [sessionStatus, setSessionStatus] = useState({ prompt_count: 0, is_locked: false });
    const [isSessionClosed, setIsSessionClosed] = useState(false);
    const [hasSavedToJournal, setHasSavedToJournal] = useState(false);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'memory'
    const [isSealingRevelation, setIsSealingRevelation] = useState(false);
    const [sealedMemoryId, setSealedMemoryId] = useState(null);
    
    // Minimum message length for Oracle
    const MIN_MESSAGE_LENGTH = 15;
    
    // Oracle persona nudge messages (when input is too short)
    const ORACLE_NUDGES = {
        love: {
            en: "Your heart is speaking, yet, the cosmos can't hear your thoughts. Can you tell me more about your feelings so I can guide you better?",
            es: "Tu corazón está hablando, pero el cosmos aún no puede escuchar tus pensamientos. ¿Podrías contarme más sobre tus sentimientos para guiarte mejor?"
        },
        dreams: {
            en: "The veil between worlds is thick. Describe the textures and colors of your dreamscape to help me pull the meaning into the light.",
            es: "El velo entre los mundos es denso. Describe las texturas y colores de tu sueño para ayudarme a traer su significado hacia la luz."
        }
    };
    
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const hasInitialized = useRef(false);
    
    // Gemini Voice Input Hook - Professional quality transcription
    const { 
        isRecording, 
        isTranscribing, 
        transcript, 
        error: voiceError, 
        isSupported: voiceSupported, 
        recordingTime,
        maxRecordingTime,
        toggleRecording, 
        clearTranscript,
        formatTime,
        languageDisplay,
        waveformData,
        languageIcon
    } = useGeminiVoiceInput(language);

    const getUserName = () => {
        try {
            const data = localStorage.getItem('astral_belonging_onboarding');
            if (data) return JSON.parse(data)?.userData?.name || '';
        } catch {}
        return '';
    };
    
    // Focus input when opening with pre-filled query
    useEffect(() => {
        if (initialQuery && inputRef.current) {
            inputRef.current.focus();
        }
    }, [initialQuery]);

    // Check session status on mount (but DON'T load history - each visit is fresh)
    useEffect(() => {
        const checkSessionStatus = async () => {
            try {
                // Map chatType to oracle_type for the session status check
                const oracleType = chatType === 'dreams' ? 'dream' : chatType;
                const headers = await getAuthHeaders();
                const statusResponse = await fetch(`${API_URL}/api/oracle/cosmos/session-status?oracle_type=${oracleType}`, { 
                    credentials: 'include',
                    headers
                });
                if (statusResponse.ok) {
                    const status = await statusResponse.json();
                    setSessionStatus(status);
                    if (status.is_locked) setIsSessionClosed(true);
                }
            } catch {}
        };
        checkSessionStatus();
    }, [chatType]);

    // Opening message - fetch from new Oracle API
    useEffect(() => {
        const fetchOracleGreeting = async () => {
            if (messages.length === 0 && !hasInitialized.current && !sessionStatus.is_locked) {
                hasInitialized.current = true;
                
                try {
                    // Map chatType to oracle_type
                    const oracleType = chatType === 'dreams' ? 'dream' : chatType;
                    const langParam = language.startsWith('es') ? 'es' : 'en';
                    
                    const headers = await getAuthHeaders();
                    const response = await fetch(`${API_URL}/api/oracle/greeting?lang=${langParam}`, {
                        method: 'POST',
                        headers: { ...headers, 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ oracle_type: oracleType })
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        setMessages([{ role: 'oracle', content: data.greeting, timestamp: new Date().toISOString() }]);
                        return;
                    }
                } catch (e) {
                    console.error("Failed to fetch Oracle greeting:", e);
                }
                
                // Fallback
                const userName = getUserName();
                const nameGreeting = userName || 'Dear one';
                let fallbackGreeting;
                if (chatType === 'love') {
                    fallbackGreeting = `${nameGreeting}, welcome to the space of connections. I am the Oracle, your advisor in matters of the heart. What brings you today?`;
                } else {
                    fallbackGreeting = `${nameGreeting}, welcome to the dream portal. I am the Oracle, your interpreter of the dream world. What have you dreamed?`;
                }
                
                setMessages([{ role: 'oracle', content: fallbackGreeting, timestamp: new Date().toISOString() }]);
            }
        };
        
        fetchOracleGreeting();
    }, [language, sessionStatus, chatType, messages.length]);

    // Voice transcript (Gemini STT)
    useEffect(() => {
        if (transcript) {
            setInputText(prev => (prev ? prev + " " + transcript : transcript).trim());
            clearTranscript();
        }
    }, [transcript, clearTranscript]);

    // Auto-scroll
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, isLoading]);

    // Auto-save on unmount - REMOVED: Now handled only in handleBack()
    // This was causing duplicate memory entries

    const handleBack = async () => {
        // Fire-and-forget: Start save in background, close immediately
        if (messages.length > 2) {
            const oracleType = chatType === 'dreams' ? 'dream' : chatType;
            const langParam = language.startsWith('es') ? 'es' : 'en';
            
            // Get auth headers for the request
            const headers = await getAuthHeaders();
            
            // Background save to Memory Pool - ONLY call this one
            fetch(`${API_URL}/api/oracle/session-end?lang=${langParam}`, {
                method: 'POST',
                headers: { 
                    ...headers,
                    'Content-Type': 'application/json' 
                },
                credentials: 'include',
                body: JSON.stringify({
                    oracle_type: oracleType,
                    history: messages.map(m => ({ role: m.role, content: m.content }))
                })
            }).catch(e => console.log('Session end save (background):', e.message));
            
            // NOTE: Removed /api/chat/save-session call - it was causing duplicate memory entries
        }
        
        // CRITICAL: Close immediately - no waiting
        onClose();
    };

    const handleSaveToJournal = async () => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_URL}/api/chat/save-session/${chatType}`, { 
                method: 'POST', 
                headers,
                credentials: 'include' 
            });
            if (response.ok) setHasSavedToJournal(true);
        } catch {}
    };
    
    // Rate Oracle response (customer feedback)
    const handleRateResponse = async (messageId, rating) => {
        try {
            const headers = await getAuthHeaders();
            const oracleType = chatType === 'dreams' ? 'dream' : chatType;
            await fetch(`${API_URL}/api/oracle/rate`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    message_id: messageId,
                    oracle_type: oracleType,
                    rating: rating
                })
            });
        } catch (e) {
            console.error("Rating failed:", e);
        }
    };

    // Seal This Revelation - Save conversation and switch to Memory tab
    const handleSealRevelation = async () => {
        if (messages.length < 2 || isSealingRevelation) return;
        
        setIsSealingRevelation(true);
        
        try {
            const oracleType = chatType === 'dreams' ? 'dream' : chatType;
            const langParam = language.startsWith('es') ? 'es' : 'en';
            const headers = await getAuthHeaders();
            
            const response = await fetch(`${API_URL}/api/oracle/session-end?lang=${langParam}`, {
                method: 'POST',
                headers: { 
                    ...headers,
                    'Content-Type': 'application/json' 
                },
                credentials: 'include',
                body: JSON.stringify({
                    oracle_type: oracleType,
                    history: messages.map(m => ({ role: m.role, content: m.content }))
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                
                // Store the new memory ID to highlight it
                if (data.insight_id) {
                    setSealedMemoryId(data.insight_id);
                }
                
                // Clear current conversation for fresh start
                setMessages([]);
                setSessionStatus({ prompt_count: 0 });
                setIsSessionClosed(false);
                setHasSavedToJournal(false);
                setGeneratedImage(null);
                
                // CRITICAL: Reset initialization flag so a new greeting will be fetched
                hasInitialized.current = false;
                
                // Switch to Memory tab to show the new summary
                setActiveTab('memory');
            }
        } catch (e) {
            console.error("Seal revelation failed:", e);
        } finally {
            setIsSealingRevelation(false);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImageError(null);
        if (file.size > 4 * 1024 * 1024) {
            setImageError(t('chat.image_too_large'));
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => setUploadedImage(event.target?.result);
        reader.readAsDataURL(file);
    };

    const handleSend = async () => {
        const text = inputText.trim();
        
        if (!text && !uploadedImage) return;
        if (isLoading || isSessionClosed) return;
        
        // Oracle persona nudge - if message too short, Oracle responds in character
        if (!uploadedImage && text.length < MIN_MESSAGE_LENGTH) {
            // Add the user's short message to chat
            setMessages(prev => [...prev, {
                role: 'user',
                content: text,
                timestamp: new Date().toISOString()
            }]);
            setInputText("");
            
            // Get the appropriate nudge for this oracle type
            const nudgeType = chatType === 'dreams' ? 'dreams' : 'love';
            const nudgeMessage = isSpanish ? ORACLE_NUDGES[nudgeType].es : ORACLE_NUDGES[nudgeType].en;
            
            // Oracle responds with a nudge (doesn't count against limit)
            setTimeout(() => {
                setMessages(prev => [...prev, {
                    role: 'oracle',
                    content: nudgeMessage,
                    timestamp: new Date().toISOString(),
                    isNudge: true // Flag so we know this doesn't count
                }]);
            }, 500); // Small delay for natural feel
            
            return;
        }

        const userMessage = {
            role: 'user',
            content: text || t('chat.image_shared'),
            imagePreview: uploadedImage,
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputText("");
        setIsLoading(true);

        try {
            let imageData = null;
            if (uploadedImage) {
                imageData = uploadedImage.includes(',') ? uploadedImage.split(',')[1] : uploadedImage;
            }
            
            // Map chatType to oracle_type
            const oracleType = chatType === 'dreams' ? 'dream' : chatType;
            
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_URL}/api/oracle/chat`, {
                method: "POST",
                headers: { ...headers, "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    message: text || "Analyze this image",
                    image_base64: imageData,
                    language: language,
                    user_name: getUserName(),
                    oracle_type: oracleType
                })
            });

            if (!response.ok) throw new Error("Failed");

            const data = await response.json();
            
            setMessages(prev => [...prev, { role: 'oracle', content: data.message, timestamp: new Date().toISOString() }]);
            setSessionStatus(prev => ({ ...prev, prompt_count: data.prompt_number }));
            
            if (data.is_closing || data.is_locked) {
                setIsSessionClosed(true);
                if (data.generated_image_url) setGeneratedImage(data.generated_image_url);
            }
            
            setUploadedImage(null);

        } catch (err) {
            console.error("Chat error:", err);
            setMessages(prev => [...prev, {
                role: 'oracle',
                content: t('chat.stars_silent'),
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <motion.div
            className="fixed inset-0 z-50 flex flex-col"
            style={{ background: theme.gradient }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {/* Session Closed Overlay */}
            <AnimatePresence>
                {isSessionClosed && (
                    <SessionClosedOverlay 
                        onSaveToJournal={handleSaveToJournal}
                        hasSaved={hasSavedToJournal}
                        theme={theme}
                        generatedImage={generatedImage}
                        t={t}
                    />
                )}
            </AnimatePresence>

            {/* Header with Tabs */}
            <div className="flex-shrink-0 border-b" style={{ borderColor: theme.accentColorLight }}>
                {/* Top row: Back button and Title */}
                <div className="p-4 pb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleBack}
                            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                            style={{ color: theme.textColor }}
                            data-testid={`${chatType}-back`}
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h2 
                                className="font-cinzel text-xl"
                                style={{ background: `linear-gradient(135deg, ${theme.textColor} 0%, white 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                            >
                                {chatType === 'love' ? t('chat.love_oracle') : t('chat.dreams_oracle')}
                            </h2>
                            <p className="text-[10px] font-inter uppercase tracking-widest" style={{ color: theme.accentColor }}>
                                {chatType === 'love' ? t('chat.love_advisor') : t('chat.dreams_interpreter')}
                            </p>
                        </div>
                    </div>
                    <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ background: theme.accentColorLight, border: `1px solid ${theme.accentColor}` }}
                    >
                        <IconComponent size={18} style={{ color: theme.textColor }} />
                    </div>
                </div>
                
                {/* Tab Buttons */}
                <div className="flex px-4 pb-2 gap-2">
                    <button
                        onClick={() => setActiveTab('chat')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all ${
                            activeTab === 'chat'
                                ? 'border'
                                : 'opacity-50 hover:opacity-70'
                        }`}
                        style={{ 
                            background: activeTab === 'chat' ? theme.accentColorLight : 'transparent',
                            borderColor: activeTab === 'chat' ? theme.accentColor : 'transparent',
                            color: theme.textColor
                        }}
                    >
                        <MessageCircle size={14} />
                        {isSpanish ? 'Conversación' : 'Conversation'}
                    </button>
                    <button
                        onClick={() => setActiveTab('memory')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all ${
                            activeTab === 'memory'
                                ? 'border'
                                : 'opacity-50 hover:opacity-70'
                        }`}
                        style={{ 
                            background: activeTab === 'memory' ? theme.accentColorLight : 'transparent',
                            borderColor: activeTab === 'memory' ? theme.accentColor : 'transparent',
                            color: theme.textColor
                        }}
                    >
                        <ScrollText size={14} />
                        {isSpanish ? 'Memorias' : 'Memories'}
                    </button>
                </div>
            </div>

            {/* Content Area - Chat or Memory Tab */}
            {activeTab === 'memory' ? (
                <OracleMemoryTab oracleType={chatType === 'dreams' ? 'dream' : chatType} onClose={() => setActiveTab('chat')} />
            ) : (
                <>
                    {/* Messages */}
                    <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4">
                        {messages.map((msg, idx) => (
                            <MessageBubble 
                                key={idx} 
                                message={msg} 
                                theme={theme}
                                onRate={handleRateResponse}
                                showRating={msg.role !== 'user' && idx > 0}
                            />
                        ))}
                        {/* Recording indicator shown inline when recording */}
                        {isRecording && <div className="text-sm italic mb-2 px-2" style={{ color: theme.accentColor }}>🎙️ Recording...</div>}
                        {isLoading && <TypingIndicator theme={theme} />}
                    </div>

                    {/* Image Preview */}
                    {uploadedImage && !isSessionClosed && (
                        <div className="px-4 py-2 border-t" style={{ borderColor: theme.accentColorLight }}>
                            <div className="relative inline-block">
                                <img src={uploadedImage} alt="Preview" className="h-16 rounded-lg opacity-80" />
                                <button onClick={() => setUploadedImage(null)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">×</button>
                            </div>
                        </div>
                    )}

                    {/* Errors */}
                    {imageError && (
                <div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                    <p className="text-xs text-red-400 flex items-center gap-2"><AlertCircle size={12} />{imageError}</p>
                </div>
            )}
            {voiceError && (
                <motion.div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <p className="text-xs text-red-400 flex items-center gap-2"><AlertCircle size={12} />{voiceError}</p>
                </motion.div>
            )}

            {/* Input Area */}
            {!isSessionClosed && (
                <div className="flex-shrink-0 p-4 border-t" style={{ borderColor: theme.accentColorLight }}>
                    {/* Seal This Revelation Button - Shows after 2+ messages */}
                    {messages.length >= 2 && (
                        <motion.button
                            onClick={handleSealRevelation}
                            disabled={isSealingRevelation}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="w-full mb-3 py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all"
                            style={{
                                background: isSealingRevelation 
                                    ? 'rgba(212, 175, 55, 0.1)' 
                                    : 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%)',
                                border: '1px solid rgba(212, 175, 55, 0.3)',
                            }}
                            whileHover={!isSealingRevelation ? { scale: 1.01, borderColor: 'rgba(212, 175, 55, 0.5)' } : {}}
                            whileTap={!isSealingRevelation ? { scale: 0.99 } : {}}
                            data-testid={`${chatType}-seal-revelation`}
                        >
                            {isSealingRevelation ? (
                                <>
                                    <Loader2 size={16} className="text-primary animate-spin" />
                                    <span className="text-primary/80 text-sm font-medium">
                                        {isSpanish ? 'Tejiendo tu revelación en el tapiz cósmico...' : 'Weaving your revelation into the cosmic tapestry...'}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Scroll size={16} className="text-primary" />
                                    <span className="text-primary text-sm font-medium">
                                        {isSpanish ? 'Sellar Esta Revelación' : 'Seal This Revelation'}
                                    </span>
                                    <span className="text-primary/50 text-xs">
                                        {isSpanish ? '• Guardar y crear imagen' : '• Save & create image'}
                                    </span>
                                </>
                            )}
                        </motion.button>
                    )}
                    
                    <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" data-testid={`${chatType}-file-input`} />
                    
                    {/* Recording Indicator */}
                    <AnimatePresence>
                        {(isRecording || isTranscribing) && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                className="flex items-center justify-center gap-3 mb-3 p-2 rounded-lg"
                                style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                            >
                                {isRecording ? (
                                    <>
                                        {/* Waveform Visualizer - CSS-based for performance */}
                                        <div className="flex items-center gap-0.5 h-6">
                                            {waveformData.length > 0 ? (
                                                waveformData.map((level, i) => (
                                                    <div
                                                        key={i}
                                                        className="w-1 rounded-full transition-all duration-75"
                                                        style={{ 
                                                            height: `${Math.max(4, level * 0.24)}px`,
                                                            minHeight: '4px', 
                                                            backgroundColor: theme.textColor 
                                                        }}
                                                    />
                                                ))
                                            ) : (
                                                <motion.div
                                                    className="w-3 h-3 rounded-full bg-red-500"
                                                    animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                                                    transition={{ duration: 1, repeat: Infinity }}
                                                />
                                            )}
                                        </div>
                                        <span className="text-red-400 text-sm font-medium">
                                            {formatTime(recordingTime)} / {formatTime(maxRecordingTime)}
                                        </span>
                                        <span className="text-red-300/80 text-sm">{languageIcon}</span>
                                    </>
                                ) : (
                                    <>
                                        <Loader2 size={16} className="animate-spin" style={{ color: theme.textColor }} />
                                        <span style={{ color: theme.textColor }} className="text-sm">{t('chat.transcribing') || 'Transcribing...'}</span>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                    
                    <div className="flex items-end gap-2 rounded-xl p-2" style={{ background: 'rgba(0, 0, 0, 0.3)', border: `1px solid ${theme.accentColorLight}` }}>
                        {/* Voice Button - Gemini Speech-to-Text */}
                        {voiceSupported && (
                            <motion.button
                                onClick={toggleRecording}
                                disabled={isTranscribing}
                                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${isRecording ? 'bg-red-500' : ''}`}
                                style={{ 
                                    background: isRecording ? undefined : theme.accentColorLight, 
                                    border: isRecording ? '2px solid rgba(255, 100, 100, 0.8)' : `1px solid ${theme.accentColor}`,
                                    boxShadow: isRecording ? '0 0 15px rgba(239, 68, 68, 0.5)' : 'none'
                                }}
                                whileHover={{ scale: isTranscribing ? 1 : 1.05 }}
                                whileTap={{ scale: isTranscribing ? 1 : 0.95 }}
                                animate={isRecording ? { scale: [1, 1.05, 1] } : {}}
                                transition={isRecording ? { duration: 1, repeat: Infinity } : {}}
                                data-testid={`${chatType}-voice-btn`}
                            >
                                {isTranscribing ? (
                                    <Loader2 size={18} className="animate-spin" style={{ color: theme.textColor }} />
                                ) : isRecording ? (
                                    <MicOff size={18} className="text-white" />
                                ) : (
                                    <Mic size={18} style={{ color: theme.textColor }} />
                                )}
                            </motion.button>
                        )}
                        <motion.button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                            style={{ background: theme.accentColorLight, border: `1px solid ${theme.accentColor}` }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            data-testid={`${chatType}-photo-btn`}
                        >
                            <Upload size={18} style={{ color: theme.textColor }} />
                        </motion.button>
                        <textarea
                            ref={inputRef}
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={isRecording 
                                ? (t('chat.recording') || 'Recording...') 
                                : isTranscribing 
                                ? (t('chat.transcribing') || 'Transcribing...') 
                                : (chatType === 'love' ? t('chat.love_placeholder') : t('chat.dreams_placeholder'))}
                            className="flex-1 bg-transparent text-white/90 text-sm font-inter resize-none placeholder:text-white/30 focus:outline-none"
                            style={{ minHeight: '40px', maxHeight: '120px' }}
                            rows={1}
                            disabled={isRecording || isTranscribing}
                            data-testid={`${chatType}-input`}
                        />
                        <motion.button
                            onClick={handleSend}
                            disabled={(!inputText.trim() && !uploadedImage) || isLoading}
                            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-30 transition-all"
                            style={{ background: theme.accentColor, border: `1px solid ${theme.textColor}` }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            data-testid={`${chatType}-send`}
                        >
                            <Send size={18} style={{ color: theme.textColor }} />
                        </motion.button>
                    </div>
                </div>
            )}
                </>
            )}
        </motion.div>
    );
};

// Export themed variants with initialQuery support
export const LoveChat = ({ onClose, initialQuery = '' }) => <UnifiedChat chatType="love" onClose={onClose} initialQuery={initialQuery} />;
export const DreamsChat = ({ onClose, initialQuery = '' }) => <UnifiedChat chatType="dreams" onClose={onClose} initialQuery={initialQuery} />;

export default UnifiedChat;
